import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Request from '../../../../../helpers/request'
import * as url from '../../../../../helpers/url_helper'
import ConnectivityIcons, { isMasterPcb } from '../../../../../components/Common/ConnectivityIcons'
import FirmwareCell from '../../../../../components/Common/FirmwareCell'
import { usePersistedPage } from '../../../../../components/Common/PaginationBar'

const SOCKET_TYPES = new Set(['98', '99'])

// Filtered view hides plug-type sockets (type 98/99) only. Raw shows all.
// role === -1 alone is NOT a hide signal — a 2Sw module can be fully
// provisioned (real MAC, real unicast) and still show role -1 because the
// Master/Backup designation is joint-dev pending.
const isUnprovisionedSocket = (pcb: any) =>
    SOCKET_TYPES.has(String(pcb.type))

const MODULE_TYPE_LABEL: Record<string, string> = {
    '0': '2Sw',
    '1': '4M',
    '2': 'Knob',
    '3': 'KnobPro',
    '4': '3R1F',
    '5': '4R',
    '98': 'Universal Plug',
    '99': 'Indian Plug',
}

// Backend convention (see backend cron.controller.js comment
// "WIFI_BLE_DUAL_PROXY — the only role with a live MQTT connection"):
// role 2 and 9 designate the Master — the PCB bridging MQTT to the BLE mesh.
// 9 is the legacy form; both are treated as Master.
const ROLE_LABEL: Record<number, string> = {
    [-1]: 'Not Set',
    2: 'Master',
    9: 'Master (legacy)',
}

const PAGE_SIZE = 8

const PaginationBar = ({ page, totalPages, total, onPage }: {
    page: number; totalPages: number; total: number; onPage: (p: number) => void;
}) => {
    if (totalPages <= 1) return null
    const from = page * PAGE_SIZE + 1
    const to = Math.min((page + 1) * PAGE_SIZE, total)
    const btn = (label: React.ReactNode, target: number, disabled: boolean, active = false) => (
        <button
            key={`${label}-${target}`}
            onClick={() => !disabled && onPage(target)}
            disabled={disabled}
            style={{
                padding: '4px 10px', fontSize: 13, fontWeight: active ? 600 : 400,
                background: active ? '#111827' : '#fff',
                color: active ? '#fff' : disabled ? '#9CA3AF' : '#374151',
                border: '1px solid #D1D5DB', borderRadius: 4,
                cursor: disabled ? 'not-allowed' : 'pointer', marginRight: 4,
            }}
        >{label}</button>
    )
    return (
        <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 16px', borderTop: '1px solid #E4E7EC', background: '#fff',
            fontSize: 13, color: '#475467',
        }}>
            <span>Showing {from}-{to} of {total}</span>
            <div>
                {btn('« Prev', page - 1, page === 0)}
                {Array.from({ length: totalPages }, (_, i) =>
                    btn(String(i + 1), i, false, i === page),
                )}
                {btn('Next »', page + 1, page >= totalPages - 1)}
            </div>
        </div>
    )
}

interface Props {
    pcbs: any[]
    allPcbs?: any[]
    allSwitches?: any[]
    allRelays?: any[]
    allRooms?: any[]
    allDevices?: any[]
    loading: boolean
    userId?: string
    placeId?: string
}

const TH = ({ children }: { children: React.ReactNode }) => (
    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 13, color: '#858D9D', fontWeight: 500, background: '#F9FAFB' }}>
        {children}
    </th>
)

const TD = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
    <td style={{ padding: '12px 16px', fontSize: 14, color: '#344054', borderTop: '1px solid #E4E7EC', ...style }}>
        {children}
    </td>
)

const ActionBtn = ({ label, color, busy, onClick, title }: {
    label: string; color: string; busy: boolean; onClick: () => void; title?: string;
}) => (
    <button
        onClick={onClick}
        disabled={busy}
        title={title || label}
        style={{
            padding: '4px 10px', fontSize: 12, fontWeight: 500,
            background: '#fff', color, border: `1px solid ${color}`,
            borderRadius: 4, cursor: busy ? 'not-allowed' : 'pointer',
            marginRight: 4, opacity: busy ? 0.5 : 1,
        }}
    >
        {label}
    </button>
)

const renderRole = (pcb: any) => {
    const role = pcb.role
    if (role == null) return '--'
    return ROLE_LABEL[role] != null ? ROLE_LABEL[role] : `${role} (?)`
}


const ModulesView = ({ pcbs, allPcbs = [], allSwitches = [], allRelays = [], allRooms = [], allDevices = [], loading, userId, placeId }: Props) => {
    const navigate = useNavigate()
    const { roomId } = useParams()
    const [viewMode, setViewMode] = useState<'filtered' | 'raw'>('filtered')
    const [busyPcbId, setBusyPcbId] = useState<string | null>(null)
    const [appliancesTarget, setAppliancesTarget] = useState<any>(null)
    const [page, setPage] = usePersistedPage(`room-modules-${placeId}-${roomId}`)
    const [watchTarget, setWatchTarget] = useState<any>(null)
    const [watchOpType, setWatchOpType] = useState<number>(2)
    const [watchMinutes, setWatchMinutes] = useState<number>(60)
    const [watchBusy, setWatchBusy] = useState(false)
    // NOTE (2026-09-01): these OTA hooks MUST sit above the `if (loading) return`
    // below — React requires an identical hook order on every render, and this
    // component first renders with loading=true. They used to live after the
    // early return, which crashed the Room page to blank once data arrived.
   // Version is DOTTED ("4.1.10"), never the encoded form: S3 publishes the
    // directory dotted and the filename with dots stripped
    // (/2SwM/h100/4.1.10/2SwM_100_4110.bin). The backend proves the artifact
    // answers 200 before it airs anything, so a typo returns a clean
    // "artifact-missing" instead of pointing a board at a 404.
    const [otaTarget, setOtaTarget] = useState<any>(null)
    const [otaChannel, setOtaChannel] = useState<string>('stable')
    const [otaVersion, setOtaVersion] = useState<string>('')
    const [otaBusy, setOtaBusy] = useState(false)

    if (loading) return <p style={{ color: '#858D9D' }}>Loading…</p>

    const relayMap: Record<string, string> = Object.fromEntries(
        allRelays.map((r: any) => [r.id, r.name]),
    )

    // Master may live in a different room — look across the whole place.
    const masterOnline = (allPcbs.length ? allPcbs : pcbs).some(
        (p: any) => isMasterPcb(p) && p.isOnline === true,
    )
    // pcb.isOnline is stale; infer module reachability from its elements
    // (any online switch on that pcbId). switch.switchIsOnline is kept fresh.
    const onlinePcbIds = new Set(
        (allSwitches || []).filter((s: any) => s.switchIsOnline === true).map((s: any) => s.pcbId),
    )
    const visiblePcbs = viewMode === 'raw'
        ? pcbs
        : pcbs.filter((p: any) => !isUnprovisionedSocket(p))
    const hiddenCount = pcbs.length - visiblePcbs.length

    const totalPages = Math.max(1, Math.ceil(visiblePcbs.length / PAGE_SIZE))
    const safePage = Math.min(page, totalPages - 1)
    const pagedPcbs = visiblePcbs.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE)

    // Raw view: group by panel, sort within by unicast (install order), and
    // order the groups themselves by min unicast within each group so the
    // visual list mirrors the actual setup sequence (per the user note about
    // "Left Side" / "Right Side" not being adjacent — install order is the
    // source of truth, not alphabetical panel name).
    type RawRow =
        | { kind: 'header'; panel: string; key: string }
        | { kind: 'pcb'; pcb: any; key: string }
    const safeUni = (p: any): number => {
        const u = p?.unicastAddress
        return u != null && u !== -1 ? u : Number.MAX_SAFE_INTEGER
    }
    const buildRawRows = (): RawRow[] => {
        if (viewMode !== 'raw') return []
        const groups = new Map<string, any[]>()
        for (const pcb of visiblePcbs) {
            const panel = relayMap[pcb.relayId] || 'Unassigned'
            if (!groups.has(panel)) groups.set(panel, [])
            groups.get(panel)!.push(pcb)
        }
        const sortedGroups = Array.from(groups.entries())
            .map(([panel, pcbs]) => {
                const sortedPcbs = pcbs.slice().sort((a: any, b: any) => safeUni(a) - safeUni(b))
                const minUni = sortedPcbs.length > 0 ? safeUni(sortedPcbs[0]) : Number.MAX_SAFE_INTEGER
                return { panel, pcbs: sortedPcbs, minUni }
            })
            .sort((a, b) => a.minUni - b.minUni)
        const rows: RawRow[] = []
        for (const g of sortedGroups) {
            rows.push({ kind: 'header', panel: g.panel, key: `hdr-${g.panel}` })
            for (const pcb of g.pcbs) {
                rows.push({ kind: 'pcb', pcb, key: `pcb-${pcb.id}` })
            }
        }
        return rows
    }
    const rawRows = buildRawRows()

    const pingPcb = (pcb: any) => {
        setBusyPcbId(pcb.id)
        Request.post(url.PING_PCB, { moduleId: pcb.id })
            .then(() => toast.success(`Ping sent to ${pcb.macAddress}`))
            .catch((err: any) => toast.error(err?.response?.data?.msg || 'Ping failed'))
            .finally(() => setBusyPcbId(null))
    }

    const restartPcb = (pcb: any) => {
        if (!window.confirm(`Restart module ${pcb.macAddress}?\n\nThis will reboot the physical hardware.`)) return
        setBusyPcbId(pcb.id)
        Request.post(url.RESTART_PCB, { moduleId: pcb.id })
            .then(() => toast.success(`Restart sent to ${pcb.macAddress}`))
            .catch((err: any) => toast.error(err?.response?.data?.msg || 'Restart failed'))
            .finally(() => setBusyPcbId(null))
    }

    // Master MAC for the watch lookup — same logic as masterOnline above:
    // master may live in a different room, look across the whole place.
    const placeMasterPcb = (allPcbs.length ? allPcbs : pcbs).find((p: any) => isMasterPcb(p))
    const placeMasterMac = placeMasterPcb?.macAddress || ''

    // -- Explicit OTA (per-row) -------------------------------------------
 
    const sendOta = () => {
        if (!otaTarget) return
        const version = otaVersion.trim()
        if (!/^\d+(\.\d+){2}$/.test(version)) {
            toast.error('Version must be dotted MAJOR.MINOR.PATCH, e.g. 4.1.10')
            return
        }
        if (!window.confirm(
            `OTA ${otaTarget.macAddress || otaTarget.id} to ${version} (${otaChannel})?\n\n`
            + `This flashes the physical module at unicast ${otaTarget.unicastAddress}.`
        )) return
        setOtaBusy(true)
        Request.post(url.OTA_TRIGGER_EXACT, {
            moduleId: otaTarget.id, channel: otaChannel, version,
        })
            .then((res: any) => {
                // The endpoint answers 200 with sent:false + a reason for every
                // refusal (artifact-missing, no-usable-unicast, v2-migration...),
                // so surface the reason rather than reporting a bare success.
                // Envelope: backend successRes -> { status, statuscode, msg, data: { result, status } },
                // and axios wraps that in .data — so the manager's verdict is res.data.data.result
                // (same depth every other caller in this tree reads, e.g. PlacePage policy).
                const r = res?.data?.data?.result || res?.data?.result || res?.result
                if (r && r.sent === false) toast.error(`OTA not sent: ${r.reason}`)
                else { toast.success(`OTA offered to ${otaTarget.macAddress || otaTarget.id}`); setOtaTarget(null) }
            })
            .catch((err: any) => toast.error(err?.response?.data?.msg || 'OTA failed'))
            .finally(() => setOtaBusy(false))
    }

    const startWatch = () => {
        if (!watchTarget || !placeMasterMac) return
        setWatchBusy(true)
        Request.post(url.HEALTH_WATCH, {
            masterMac: placeMasterMac,
            uni: watchTarget.unicastAddress,
            opType: watchOpType,
            minutes: watchMinutes,
        })
            .then(() => {
                toast.success(`Watching ${watchTarget.macAddress || watchTarget.id}`)
                setWatchTarget(null)
            })
            .catch((err: any) => toast.error(err?.response?.data?.msg || 'Failed to start watch'))
            .finally(() => setWatchBusy(false))
    }

    const toggleBtn = (mode: 'filtered' | 'raw', label: string) => (
        <button
            onClick={() => setViewMode(mode)}
            style={{
                padding: '4px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                background: viewMode === mode ? '#111827' : '#fff',
                color: viewMode === mode ? '#fff' : '#374151',
                border: '1px solid #D1D5DB',
                borderRadius: 4, marginRight: 4,
            }}
        >
            {label}
        </button>
    )

    // Extracted so Raw (grouped) and Filtered (paginated) views render the
    // same row markup without duplicating ~50 lines.
    const renderPcbRow = (pcb: any, idx: number = 0) => {
        const naSocket = isUnprovisionedSocket(pcb)
        const NA = <span style={{ color: '#858D9D' }}>NA</span>
        return (
            <tr key={pcb.id || `row-${idx}`}>
                <TD>{relayMap[pcb.relayId] || '--'}</TD>
                <TD>{MODULE_TYPE_LABEL[String(pcb.type)] ?? pcb.type ?? '--'}</TD>
                <TD>{naSocket ? NA : <code style={{ fontSize: 12 }}>{pcb.macAddress || '--'}</code>}</TD>
                <TD>{naSocket ? NA : (pcb.unicastAddress != null && pcb.unicastAddress !== -1 ? pcb.unicastAddress : '--')}</TD>
                <TD>{naSocket ? NA : renderRole(pcb)}</TD>
                <TD>{naSocket ? NA : (pcb.hardwareVersion || '--')}</TD>
                <TD>{naSocket ? NA : <FirmwareCell pcb={pcb} />}</TD>
                <TD>{naSocket ? NA : <ConnectivityIcons pcb={pcb} masterOnline={masterOnline} moduleOnline={onlinePcbIds.has(pcb.id)} />}</TD>
                <TD>
                    {naSocket ? NA : (
                        <>
                            <ActionBtn label="Ping" color="#1a73e8" busy={busyPcbId === pcb.id} onClick={() => pingPcb(pcb)} />
                            <ActionBtn label="Restart" color="#B54708" busy={busyPcbId === pcb.id} onClick={() => restartPcb(pcb)} />
                            <ActionBtn label="Appliances" color="#107569" busy={false} onClick={() => setAppliancesTarget(pcb)} title="Show appliances controlled by this module" />
                            <ActionBtn label="Logs" color="#475467" busy={false} onClick={() => { if (userId && placeId) navigate(`/users/profile/${userId}/places/${placeId}/logs`) }} title="Open the Logs tab for this place" />
                            <ActionBtn label="Watch" color="#7F56D9" busy={isMasterPcb(pcb) || !placeMasterMac} onClick={() => setWatchTarget(pcb)} title={isMasterPcb(pcb) ? 'Master sends periodic health automatically' : (!placeMasterMac ? 'No master MAC in this place' : 'Watch this node\'s health for a TTL-boxed window')} />
                            <ActionBtn label="OTA" color="#B42318" busy={otaBusy && otaTarget?.id === pcb.id} onClick={() => { setOtaTarget(pcb); setOtaVersion(''); }} title="Flash an exact channel + version to this module" />
                        </>
                    )}
                </TD>
            </tr>
        )
    }

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                {toggleBtn('filtered', 'Filtered')}
                {toggleBtn('raw', 'Raw')}
                {viewMode === 'filtered' && hiddenCount > 0 && (
                    <span style={{ fontSize: 12, color: '#858D9D' }}>
                        Hiding {hiddenCount} plug{hiddenCount === 1 ? '' : 's'} / socket{hiddenCount === 1 ? '' : 's'}
                    </span>
                )}
            </div>
            <div style={{ background: '#fff', border: '1px solid #E4E7EC', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <TH>Panel</TH>
                            <TH>Type</TH>
                            <TH>MAC<br />Address</TH>
                            <TH>Unicast<br />Address</TH>
                            <TH>Role</TH>
                            <TH>HW<br />Version</TH>
                            <TH>FW<br />Version</TH>
                            <TH>Connectivity Status<br />(WiFi/Mesh)</TH>
                            <TH>Actions</TH>
                        </tr>
                    </thead>
                    <tbody>
                        {visiblePcbs.length === 0 && (
                            <tr>
                                <td colSpan={9} style={{ padding: 20, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>
                                    No modules in this room
                                </td>
                            </tr>
                        )}
                        {viewMode === 'filtered' && pagedPcbs.map((pcb, idx) => renderPcbRow(pcb, idx))}
                        {viewMode === 'raw' && rawRows.map(row => row.kind === 'header' ? (
                            <tr key={row.key} data-testid={`panel-group-${row.panel}`}>
                                <td
                                    colSpan={9}
                                    style={{
                                        background: '#F2F4F7', padding: '8px 16px',
                                        fontSize: 13, fontWeight: 600, color: '#344054',
                                        borderTop: '1px solid #E4E7EC',
                                    }}
                                >
                                    {row.panel}
                                </td>
                            </tr>
                        ) : renderPcbRow(row.pcb))}
                    </tbody>
                </table>
                {viewMode === 'filtered' && (
                    <PaginationBar
                        page={safePage}
                        totalPages={totalPages}
                        total={visiblePcbs.length}
                        onPage={setPage}
                    />
                )}
            </div>

            {otaTarget && (
                <div
                    data-testid="ota-dialog"
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                    }}
                    onClick={() => !otaBusy && setOtaTarget(null)}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: '#fff', borderRadius: 12, padding: 24,
                            width: 420, maxWidth: '90%', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                        }}
                    >
                        <h5 style={{ marginBottom: 6 }}>Flash firmware</h5>
                        <p style={{ fontSize: 13, color: '#858D9D', marginBottom: 16 }}>
                            <code style={{ fontSize: 13 }}>{otaTarget.macAddress || otaTarget.id}</code>
                            {otaTarget.unicastAddress != null && otaTarget.unicastAddress !== -1
                                ? ` · unicast ${otaTarget.unicastAddress}` : ''}
                            {otaTarget.firmwareVersion ? ` · now ${otaTarget.firmwareVersion}` : ''}
                        </p>
                        <div style={{ marginBottom: 12 }}>
                            <label style={{ display: 'block', fontSize: 12, color: '#858D9D', marginBottom: 4 }}>Channel</label>
                            <select
                                data-testid="ota-channel"
                                value={otaChannel}
                                onChange={e => setOtaChannel(e.target.value)}
                                disabled={otaBusy}
                                style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid #D0D5DD', borderRadius: 6 }}
                            >
                                <option value="stable">stable</option>
                                <option value="beta">beta</option>
                                <option value="alpha">alpha</option>
                            </select>
                        </div>
                        <div style={{ marginBottom: 20 }}>
                            <label style={{ display: 'block', fontSize: 12, color: '#858D9D', marginBottom: 4 }}>Version</label>
                            <input
                                data-testid="ota-version"
                                value={otaVersion}
                                onChange={e => setOtaVersion(e.target.value)}
                                disabled={otaBusy}
                                placeholder="4.1.10"
                                style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid #D0D5DD', borderRadius: 6 }}
                            />
                            <div style={{ fontSize: 11, color: '#858D9D', marginTop: 4 }}>
                                Dotted, e.g. <code>4.1.10</code> &mdash; not <code>4110</code>. The server refuses if that build is not published.
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setOtaTarget(null)}
                                disabled={otaBusy}
                                style={{ padding: '8px 14px', fontSize: 13, border: '1px solid #D0D5DD', borderRadius: 6, background: '#fff' }}
                            >Cancel</button>
                            <button
                                data-testid="ota-send"
                                onClick={sendOta}
                                disabled={otaBusy}
                                style={{ padding: '8px 14px', fontSize: 13, border: 0, borderRadius: 6, background: '#B42318', color: '#fff', opacity: otaBusy ? 0.5 : 1 }}
                            >{otaBusy ? 'Sending…' : 'Flash'}</button>
                        </div>
                    </div>
                </div>
            )}

            {watchTarget && (
                <div
                    data-testid="watch-dialog"
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                    }}
                    onClick={() => !watchBusy && setWatchTarget(null)}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: '#fff', borderRadius: 12, padding: 24,
                            width: 420, maxWidth: '90%', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                        }}
                    >
                        <h5 style={{ marginBottom: 6 }}>Watch health</h5>
                        <p style={{ fontSize: 13, color: '#858D9D', marginBottom: 16 }}>
                            <code style={{ fontSize: 13 }}>{watchTarget.macAddress || watchTarget.id}</code>
                            {watchTarget.unicastAddress != null && watchTarget.unicastAddress !== -1
                                ? ` · unicast ${watchTarget.unicastAddress}` : ''}
                        </p>
                        <div style={{ marginBottom: 12 }}>
                            <label style={{ display: 'block', fontSize: 12, color: '#858D9D', marginBottom: 4 }}>Read type</label>
                            <select
                                data-testid="watch-optype"
                                value={watchOpType}
                                onChange={e => setWatchOpType(Number(e.target.value))}
                                style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid #D0D5DD', borderRadius: 6 }}
                            >
                                <option value={2}>Memory (default)</option>
                                <option value={1}>Coil</option>
                                <option value={3}>Tasks</option>
                            </select>
                        </div>
                        <div style={{ marginBottom: 20 }}>
                            <label style={{ display: 'block', fontSize: 12, color: '#858D9D', marginBottom: 4 }}>Duration</label>
                            <select
                                data-testid="watch-duration"
                                value={watchMinutes}
                                onChange={e => setWatchMinutes(Number(e.target.value))}
                                style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid #D0D5DD', borderRadius: 6 }}
                            >
                                <option value={15}>15 minutes</option>
                                <option value={60}>1 hour</option>
                                <option value={240}>4 hours</option>
                                <option value={1440}>24 hours</option>
                            </select>
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setWatchTarget(null)}
                                disabled={watchBusy}
                                style={{ padding: '8px 16px', fontSize: 14, background: '#fff', color: '#374151', border: '1px solid #D1D5DB', borderRadius: 6, cursor: watchBusy ? 'not-allowed' : 'pointer' }}
                            >
                                Cancel
                            </button>
                            <button
                                data-testid="watch-start-btn"
                                onClick={startWatch}
                                disabled={watchBusy}
                                style={{ padding: '8px 16px', fontSize: 14, background: '#7F56D9', color: '#fff', border: 'none', borderRadius: 6, cursor: watchBusy ? 'not-allowed' : 'pointer' }}
                            >
                                {watchBusy ? 'Starting…' : 'Start watch'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {appliancesTarget && (() => {
                const pcbSwitches = (allSwitches || []).filter((s: any) => s.pcbId === appliancesTarget.id)
                // Build the appliance list: distinct devices controlled by
                // switches on this module. Plug-type switches (98/99) without
                // a device link are themselves the appliance.
                const deviceById: Record<string, any> = Object.fromEntries(
                    (allDevices || []).map((d: any) => [d.id, d]),
                )
                const seen = new Set<string>()
                const moduleAppliances: Array<{ key: string; name: string; online: boolean | null }> = []
                for (const s of pcbSwitches) {
                    if (s.deviceId && deviceById[s.deviceId]) {
                        if (seen.has(s.deviceId)) continue
                        seen.add(s.deviceId)
                        const d = deviceById[s.deviceId]
                        const ctrl = pcbSwitches.filter((x: any) => x.deviceId === s.deviceId)
                        const online = ctrl.some((x: any) => x.switchIsOnline === true) ? true
                            : ctrl.some((x: any) => x.switchIsOnline === false) ? false : null
                        moduleAppliances.push({ key: d.id, name: d.name || '--', online })
                    } else if (Number(s.type) === 98 || Number(s.type) === 99) {
                        moduleAppliances.push({ key: s.id, name: s.name || '--', online: s.switchIsOnline ?? null })
                    }
                }
                return (
                    <div
                        data-testid="switches-dialog"
                        style={{
                            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                        }}
                        onClick={() => setAppliancesTarget(null)}
                    >
                        <div
                            onClick={e => e.stopPropagation()}
                            style={{
                                background: '#fff', borderRadius: 12, padding: 24,
                                maxWidth: 640, width: '90%', maxHeight: '80vh', overflow: 'auto',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                            }}
                        >
                            <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                                Appliances on <code style={{ fontSize: 13 }}>{appliancesTarget.macAddress}</code>
                            </h4>
                            <div style={{ marginTop: 12, fontSize: 13, color: '#6B7280' }}>
                                {moduleAppliances.length} appliance{moduleAppliances.length === 1 ? '' : 's'} on this module
                            </div>
                            {moduleAppliances.length > 0 ? (
                                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
                                    <thead>
                                        <tr>
                                            <TH>Appliance Name</TH>
                                            <TH>Online</TH>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {moduleAppliances.map(a => (
                                            <tr key={a.key}>
                                                <TD>{a.name}</TD>
                                                <TD>
                                                    {a.online === true && <span style={{ color: '#16A34A' }}>● Online</span>}
                                                    {a.online === false && <span style={{ color: '#9CA3AF' }}>○ Offline</span>}
                                                    {a.online == null && <span style={{ color: '#858D9D' }}>--</span>}
                                                </TD>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div style={{ padding: 20, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>
                                    No appliances on this module
                                </div>
                            )}
                            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                                <button
                                    onClick={() => setAppliancesTarget(null)}
                                    style={{ padding: '8px 16px', fontSize: 14, background: '#fff', color: '#374151', border: '1px solid #D1D5DB', borderRadius: 6, cursor: 'pointer' }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )
            })()}
        </div>
    )
}

export default ModulesView
